import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  differenceInMilliseconds,
  formatISO9075,
  fromUnixTime,
  isFuture,
  isPast,
} from 'date-fns'

import { eventCategory } from '@/constants/analytics'
import { eventStatus, fetchStatus } from '@/constants/common'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { ApiService } from '@/service'
import useStore from '@/store/useStore'
import {
  ChannelType,
  EventType,
  LineType,
} from '@/types/servicesTypes/configTypes'
import { SportCategoryType } from '@/types/servicesTypes/sportCategoriesTypes'
import { EventWithHistoryType } from '@/types/storeTypes/commonStoreTypes'
import { sendEventToGTAG } from '@/utils/utils'
export const useEventDetailsGA = (
  event: Nullable<EventWithHistoryType>,
  channels: Nullable<Array<ChannelType>>,
) => {
  useEffect(() => {
    if (event && channels) {
      const currentChannel = channels?.find((c) => c.id === event.channelId)
      if (currentChannel) {
        const currentChannelName =
          currentChannel.name || currentChannel.nameLong
        const eventStartTimeHuman =
          event && formatISO9075(fromUnixTime(event.startTime))
        sendEventToGTAG(eventCategory.DETAIL_PAGE, {
          name: event?.name,
          date: eventStartTimeHuman,
          channel: currentChannelName,
          category: event?.categoryId,
        })
      }
    }
  }, [event, channels])
}

export const useGetMemoizedLines = () => {
  const currentEvent = useStore((state) => state.common.currentEvent)

  const [category, setCategory] = useState<Nullable<SportCategoryType>>(null)
  const [status, setStatus] = useState(fetchStatus.IDLE)

  const { t } = useCommonTranslation()
  const liveTodayLocale = t('liveToday')

  useEffect(() => {
    const controller = new AbortController()
    const abortSignal = controller.signal
    if (currentEvent && !currentEvent.categoryId) {
      setCategory(null)
      setStatus(fetchStatus.COMPLETED)
    }
    if (!currentEvent?.categoryId || category?.id === currentEvent.categoryId) {
      return
    }

    setStatus(fetchStatus.FETCHING)
    ApiService.getSportCategoryById(currentEvent.categoryId, abortSignal)
      .then((res) => {
        if (!controller.signal.aborted) {
          setCategory(res)
        }

        setStatus(fetchStatus.COMPLETED)
      })
      .catch(() => {
        setCategory(null)
        setStatus(fetchStatus.FAILED)
      })

    return () => {
      controller.abort()
    }
  }, [currentEvent])

  const memoizedLines: LineType[] = useMemo((): LineType[] => {
    if (category) {
      return [
        {
          lineContextId: category.liveTodayLineContextId,
          name: `${liveTodayLocale} IN ${category.name}`,
          liveToday: true,
        },
      ]
    } else {
      return []
    }
  }, [category])
  return { category, status, memoizedLines }
}

export const useEventStatus = (event: Nullable<EventType>) => {
  const [currentEventStatus, setCurrentEventStatus] =
    useState<Nullable<eventStatus>>(null)
  const timeoutId = useRef<NodeJS.Timeout>()

  const startTime = useMemo(
    () => event && fromUnixTime(event.startTime),
    [event],
  )
  const endTime = useMemo(() => event && fromUnixTime(event.endTime), [event])

  useEffect(() => {
    if (event) {
      setCurrentEventStatus(getStatus())
    }
    return () => {
      clearTimeout(timeoutId.current)
      setCurrentEventStatus(null)
    }
  }, [event])

  useEffect(() => {
    if (currentEventStatus) {
      addTimeout()
    }
  }, [currentEventStatus, endTime, startTime])

  const addTimeout = useCallback(() => {
    timeoutId.current && clearTimeout(timeoutId.current)
    const timeoutEndTime =
      currentEventStatus === eventStatus.IS_LIVE
        ? endTime
        : currentEventStatus === eventStatus.IS_FUTURE
        ? startTime
        : 0
    if (timeoutEndTime) {
      const remainingTime = differenceInMilliseconds(timeoutEndTime, new Date())
      timeoutId.current = setTimeout(() => {
        const status = getStatus()
        if (currentEventStatus === status) {
          addTimeout()
        } else {
          setCurrentEventStatus(status)
        }
      }, remainingTime)
    }
  }, [currentEventStatus, startTime, endTime])

  const getStatus = useCallback(() => {
    if (!startTime || !endTime) {
      return null
    }
    return isPast(startTime) && isFuture(endTime)
      ? eventStatus.IS_LIVE
      : isFuture(endTime)
      ? eventStatus.IS_FUTURE
      : eventStatus.IS_PAST
  }, [startTime, endTime])

  return currentEventStatus
}
